import { useState } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Link } from 'react-router';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { AuthCallbackPage } from '@/pages/auth/AuthCallbackPage';
import { ChapterLandingPage } from '@/pages/public/ChapterLandingPage';
import { LandingRouter } from '@/components/common/LandingRouter';
import { ScopeProvider } from '@/contexts/ScopeContext';
import { ScopeSwitcher } from '@/components/common/ScopeSwitcher';
import { ChapterLayout } from '@/layouts/ChapterLayout';
import { BranchLayout } from '@/layouts/BranchLayout';
import { ScopedAdminLayout } from '@/layouts/ScopedAdminLayout';
import { ScopedAdminOverview } from '@/pages/admin/ScopedAdminOverview';
import { EventRegisterPage } from '@/pages/public/EventRegisterPage';
import { VerifyPage } from '@/pages/public/VerifyPage';
import { PrivacyPage } from '@/pages/public/PrivacyPage';
import { TermsPage } from '@/pages/public/TermsPage';
import { ContactPage } from '@/pages/public/ContactPage';
import { ContentEditorPage } from '@/pages/admin/ContentEditorPage';
import { PhotoApprovalPage } from '@/pages/admin/PhotoApprovalPage';
import { SurveyBuilderPage } from '@/pages/admin/SurveyBuilderPage';
import { SurveyResultsPage } from '@/pages/admin/SurveyResultsPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { RegistrationsPage } from '@/pages/admin/RegistrationsPage';
import { MembersPage } from '@/pages/admin/MembersPage';
import { SuperAdminPage } from '@/pages/admin/SuperAdminPage';
import { GroupMembersPage } from '@/pages/admin/GroupMembersPage';
import { GroupsPage } from '@/pages/admin/GroupsPage';
import { InvitationsPage } from '@/pages/admin/InvitationsPage';
import { BranchJoinRequestsPage } from '@/pages/admin/BranchJoinRequestsPage';
import { PageEditor } from '@/pages/admin/PageEditor';
import { GalleryAdminPage } from '@/pages/admin/GalleryAdminPage';
import { EventEditorPage } from '@/pages/admin/EventEditorPage';
import { EventListPage } from '@/pages/events/EventListPage';
import { EventDetailPage } from '@/pages/events/EventDetailPage';
// spec-140 phase 5: Projects UI removed. Tasks now live under
// Groups (GroupTasksPanel). Backend project routes still exist for
// legacy callers but no UI surfaces them.
import { InviteAcceptPage } from '@/pages/invitations/InviteAcceptPage';
import { SharePage } from '@/pages/share/SharePage';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { MemberLayout } from '@/layouts/MemberLayout';
import { MemberRedirect } from '@/pages/MemberRedirect';
import { TaskListDetailPage } from '@/pages/member/TaskListDetailPage';
import { LegacyMessagesRedirect } from '@/pages/member/LegacyMessagesRedirect';
import { ChapterPageView } from '@/pages/member/ChapterPageView';
import { GroupView } from '@/pages/member/GroupView';
import { AdminLayout } from '@/layouts/AdminLayout';
import { DashboardPage } from '@/pages/member/DashboardPage';
import { MessagesPage } from '@/pages/member/MessagesPage';
import { PhotosPage } from '@/pages/member/PhotosPage';
import { SurveyPage } from '@/pages/member/SurveyPage';
import { SettingsPage } from '@/pages/member/SettingsPage';

/**
 * Root layout component that wraps all pages
 * Provides the main navigation and footer structure with mobile-responsive hamburger menu
 */
function RootLayout() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    await logout();
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 glass">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" onClick={closeMobileMenu}>
            <span className="text-2xl font-display font-bold text-gradient">Re:Nav-96</span>
          </Link>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/80 hover:text-white transition-colors">
              Home
            </Link>
            {isLoading ? (
              <span className="text-white/60 text-sm px-4 py-2">Loading...</span>
            ) : isAuthenticated && user ? (
              <>
                <Link
                  to="/member/dashboard"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  Dashboard
                </Link>
                {user.role === 'super_admin' && (
                  <Link
                    to="/admin"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Admin
                  </Link>
                )}
                <ScopeSwitcher />
                <div className="flex items-center gap-3">
                  <span className="text-white/80 text-sm">{user.displayName}</span>
                  <button
                    onClick={handleLogout}
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/signup" className="text-white/80 hover:text-white transition-colors">
                  Sign up
                </Link>
                <Link to="/login" className="btn-primary text-sm px-4 py-2">
                  Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button - Visible on mobile only */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white transition-colors"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-secondary-dark/95 backdrop-blur-lg">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="text-white/80 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
              >
                Home
              </Link>
              {isLoading ? (
                <span className="text-white/60 text-sm py-2 px-3">Loading...</span>
              ) : isAuthenticated && user ? (
                <>
                  <Link
                    to="/member/dashboard"
                    onClick={closeMobileMenu}
                    className="text-white/80 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
                  >
                    Dashboard
                  </Link>
                  {user.role === 'super_admin' && (
                    <Link
                      to="/admin"
                      onClick={closeMobileMenu}
                      className="text-white/80 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
                    >
                      Admin
                    </Link>
                  )}
                  <div className="border-t border-white/10 pt-3 mt-2">
                    <div className="flex items-center justify-between px-3">
                      <span className="text-white/80 text-sm">{user.displayName}</span>
                      <button
                        onClick={handleLogout}
                        className="btn-secondary text-sm px-4 py-2"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    onClick={closeMobileMenu}
                    className="text-white/80 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
                  >
                    Sign up
                  </Link>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="btn-primary text-sm px-4 py-2 text-center mt-2"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-secondary-dark py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-white/60 text-sm">
                Re:Nav-96 - Navarasam School 1996 Batch
              </p>
              <p className="text-white/40 text-xs mt-1">
                30th Year Reunion - June 13th, 2026
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm text-white/60">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


/**
 * Placeholder for pages not yet implemented
 */
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center">
        <h1 className="text-4xl font-display font-bold mb-4">{title}</h1>
        <p className="text-white/60">This page is coming soon.</p>
        <Link to="/" className="link mt-4 inline-block">
          Back to Home
        </Link>
      </div>
    </div>
  );
}

/**
 * 404 Not Found page
 */
function NotFoundPage() {
  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center">
        <h1 className="text-6xl font-display font-bold text-primary mb-4">404</h1>
        <p className="text-xl text-white/80 mb-8">Page not found</p>
        <Link to="/" className="btn-primary">
          Go Home
        </Link>
      </div>
    </div>
  );
}

/**
 * Application router configuration using React Router v7
 *
 * Route structure:
 * - / (public routes with RootLayout)
 *   - / (HomePage)
 *   - /register (Event registration)
 *   - /login (User login)
 *   - /signup (User account registration)
 *   - /verify (Email/phone verification)
 * - /member (protected member routes - requires authentication)
 *   - /dashboard (Member dashboard)
 *   - /photos (Photo gallery)
 *   - /messages (Message boards)
 *   - /surveys/:id (Survey response page)
 * - /admin (super admin routes)
 *   - / (Admin dashboard)
 *   - /registrations (Registration management with export)
 *   - /content (Dynamic content editor)
 *   - /photos (Photo approval queue)
 *   - /surveys/new (Survey form builder - create new)
 *   - /surveys/:id/results (Survey results data grid with export)
 *   - /surveys/:id (Survey form builder - edit existing)
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <LandingRouter />,
      },
      // Scope-aware chapter routes — single ScopeProvider wrapping the chapter sub-tree.
      {
        path: 'c/:chapterSlug',
        element: (
          <ScopeProvider>
            <ChapterLayout />
          </ScopeProvider>
        ),
        children: [
          { index: true, element: <ChapterLandingPage /> },
          { path: 'events', element: <EventListPage /> },
          { path: 'events/:eventSlug', element: <EventDetailPage /> },
        ],
      },
      // Scoped admin shell under the chapter. Lives outside ChapterLayout
      // so it gets its own sidebar instead of the public chapter sub-nav.
      {
        path: 'c/:chapterSlug/admin',
        element: (
          <ProtectedRoute>
            <ScopeProvider>
              <ScopedAdminLayout />
            </ScopeProvider>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <ScopedAdminOverview /> },
          { path: 'pages', element: <PageEditor /> },
          { path: 'gallery', element: <GalleryAdminPage /> },
          { path: 'events', element: <EventEditorPage /> },
          { path: 'invitations', element: <InvitationsPage /> },
          { path: 'join-requests', element: <BranchJoinRequestsPage /> },
        ],
      },
      // Branch sub-routes share the same ScopeProvider tree but resolve a branch via slugs.
      {
        path: 'c/:chapterSlug/b/:branchSlug',
        element: (
          <ScopeProvider>
            <BranchLayout />
          </ScopeProvider>
        ),
        children: [
          { index: true, element: <ChapterLandingPage /> },
          { path: 'events', element: <EventListPage /> },
          { path: 'events/:eventSlug', element: <EventDetailPage /> },
        ],
      },
      {
        path: 'register',
        element: <EventRegisterPage />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'signup',
        element: <RegisterPage />,
      },
      {
        path: 'auth/callback',
        element: <AuthCallbackPage />,
      },
      {
        path: 'verify',
        element: <VerifyPage />,
      },
      {
        path: 'i/:code',
        element: <InviteAcceptPage />,
      },
      {
        path: 'g/share/:token',
        element: <SharePage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      // Legacy /member/* paths redirect into the chapter-scoped tree.
      {
        path: 'member',
        element: (
          <ProtectedRoute>
            <MemberRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: 'member/*',
        element: (
          <ProtectedRoute>
            <MemberRedirect />
          </ProtectedRoute>
        ),
      },
      // Chapter-scoped member surfaces. Mounting MemberLayout here makes
      // the chapterSlug param available to children via useParams.
      {
        path: 'c/:chapterSlug/m',
        element: (
          <ProtectedRoute>
            <MemberLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'photos', element: <PhotosPage /> },
          // Groups is the new canonical name; messages is kept as a
          // legacy redirect.
          { path: 'groups', element: <MessagesPage /> },
          { path: 'groups/:groupId', element: <GroupView /> },
          { path: 'messages', element: <LegacyMessagesRedirect /> },
          { path: 'messages/:groupId', element: <LegacyMessagesRedirect /> },
          { path: 'surveys/:id', element: <SurveyPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'tasks/:listId', element: <TaskListDetailPage /> },
          { path: 'p/:pageSlug', element: <ChapterPageView /> },
          { path: '*', element: <PlaceholderPage title="Member Area" /> },
        ],
      },
      // Admin routes - super admin authorization required
      {
        path: 'admin',
        element: (
          <ProtectedRoute requiredRole="super_admin">
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboardPage />,
          },
          {
            path: 'registrations',
            element: <RegistrationsPage />,
          },
          {
            path: 'members',
            element: <MembersPage />,
          },
          {
            path: 'super',
            element: <SuperAdminPage />,
          },
          {
            path: 'groups',
            element: <GroupsPage />,
          },
          {
            path: 'groups/:groupId/members',
            element: <GroupMembersPage />,
          },
          {
            path: 'invitations',
            element: <InvitationsPage />,
          },
          {
            path: 'join-requests',
            element: <BranchJoinRequestsPage />,
          },
          {
            path: 'pages',
            element: <PageEditor />,
          },
          {
            path: 'gallery',
            element: <GalleryAdminPage />,
          },
          {
            path: 'events',
            element: <EventEditorPage />,
          },
          {
            path: 'content',
            element: <ContentEditorPage />,
          },
          {
            path: 'photos',
            element: <PhotoApprovalPage />,
          },
          {
            path: 'surveys/new',
            element: <SurveyBuilderPage />,
          },
          {
            path: 'surveys/:id/results',
            element: <SurveyResultsPage />,
          },
          {
            path: 'surveys/:id',
            element: <SurveyBuilderPage />,
          },
          // Catch-all for other admin routes
          {
            path: '*',
            element: <PlaceholderPage title="Admin Area" />,
          },
        ],
      },
      // 404 catch-all
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

/**
 * Main App component
 * Wraps the application with AuthProvider and RouterProvider for React Router v7
 */
function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}

export default App;
